import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';

/**
 * Protected route component that checks for user authentication and role-based access
 * @param {Object} props - Component props
 * @param {Component} props.element - The component to render if authorized
 * @param {string|string[]} props.requiredRoles - Optional roles required to access this route
 * @returns {JSX.Element} - The component or redirect
 */
const ProtectedRoute = ({ element: Component, requiredRoles, ...rest }) => {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check token on every route change or component mount
    const storedToken = localStorage.getItem('userToken_TOVIAPPS');
    
    // If no token in localStorage, redirect to login
    if (!storedToken) {
      navigate('/login', { replace: true });
      return;
    }
    
    // If token in context doesn't match localStorage token
    if (!user || user.token !== storedToken) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Check if user is logged in
  if (!user || !user.token) {
    return <Navigate to="/login" replace />;
  }

  // If no specific roles are required, allow access
  if (!requiredRoles) {
    return <Component {...rest} />;
  }

  // Check if the user has Master or Admin role which gives full access
  if (user.permissions && (user.permissions.includes('Master') || user.permissions.includes('Admin'))) {
    return <Component {...rest} />;
  }

  // Convert requiredRoles to array if it's a string
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  // Check if user has any of the required roles
  const hasRequiredRole = user.permissions && 
    user.permissions.some(role => roles.includes(role));

  if (hasRequiredRole) {
    return <Component {...rest} />;
  }

  // Redirect to main page if user doesn't have required roles
  // Include a message about insufficient permissions
  return (
    <Navigate 
      to="/main" 
      state={{ 
        from: location,
        permissionError: `Você não tem permissão para acessar ${location.pathname}` 
      }} 
      replace 
    />
  );
};

export default ProtectedRoute;